import * as THREE from "three";

/**
 * Efectos para `Volcan3.glb` (adaptación de `public/models/main.js`).
 * Importante: NO crea renderer/cámara/DOM. Solo modifica el grafo del modelo y
 * expone `update(delta, t)` para llamarlo desde el loop de MindAR.
 */

function getTextureName(texture) {
  if (!texture) return "";
  if (texture.name) return texture.name;
  if (texture.source?.data?.currentSrc) return texture.source.data.currentSrc;
  if (texture.source?.data?.src) return texture.source.data.src;
  if (texture.image?.currentSrc) return texture.image.currentSrc;
  if (texture.image?.src) return texture.image.src;
  return "";
}

function getAnimationGroup(textureNameLower, materialNameLower, meshNameLower) {
  const isMagma =
    textureNameLower.includes("magma_flow") ||
    textureNameLower.includes("magma flow") ||
    materialNameLower.includes("magma_flow") ||
    materialNameLower.includes("magma flow") ||
    meshNameLower.includes("magma_flow") ||
    meshNameLower.includes("magma flow");
  if (isMagma) return "magma";

  const isLava =
    textureNameLower.includes("lava_flow") ||
    materialNameLower.includes("lava_flow") ||
    meshNameLower.includes("lava_flow");
  if (isLava) return "lava";

  const isChimney =
    textureNameLower.includes("chimmey") ||
    textureNameLower.includes("chimney") ||
    materialNameLower.includes("chimmey") ||
    materialNameLower.includes("chimney") ||
    meshNameLower.includes("chimmey") ||
    meshNameLower.includes("chimney");
  if (isChimney) return "chimney";

  return "";
}

function attachProceduralSmoke(material, smokeProceduralMaterials) {
  if (!material || material.userData?.hasProceduralSmoke) return;

  material.userData.hasProceduralSmoke = true;
  material.userData.smokeUniforms = {
    uSmokeTime: { value: 0 },
  };

  material.transparent = true;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uSmokeTime = material.userData.smokeUniforms.uSmokeTime;

    shader.vertexShader =
      `varying vec3 vSmokeLocalPos;\n` +
      shader.vertexShader.replace(
        "#include <project_vertex>",
        `#include <project_vertex>
vSmokeLocalPos = transformed;`
      );

    shader.fragmentShader =
      `
uniform float uSmokeTime;
varying vec3 vSmokeLocalPos;

float smokeHash(vec3 p) {
  return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453123);
}

float smokeNoise(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);

  float n000 = smokeHash(i + vec3(0.0, 0.0, 0.0));
  float n100 = smokeHash(i + vec3(1.0, 0.0, 0.0));
  float n010 = smokeHash(i + vec3(0.0, 1.0, 0.0));
  float n110 = smokeHash(i + vec3(1.0, 1.0, 0.0));
  float n001 = smokeHash(i + vec3(0.0, 0.0, 1.0));
  float n101 = smokeHash(i + vec3(1.0, 0.0, 1.0));
  float n011 = smokeHash(i + vec3(0.0, 1.0, 1.0));
  float n111 = smokeHash(i + vec3(1.0, 1.0, 1.0));

  float nx00 = mix(n000, n100, f.x);
  float nx10 = mix(n010, n110, f.x);
  float nx01 = mix(n001, n101, f.x);
  float nx11 = mix(n011, n111, f.x);
  float nxy0 = mix(nx00, nx10, f.y);
  float nxy1 = mix(nx01, nx11, f.y);
  return mix(nxy0, nxy1, f.z);
}
` +
      shader.fragmentShader.replace(
        "#include <alphamap_fragment>",
        `#include <alphamap_fragment>
vec3 smokeP1 = vSmokeLocalPos * 0.02 + vec3(uSmokeTime * 0.25, -uSmokeTime * .8, 0.0);
vec3 smokeP2 = vSmokeLocalPos * 0.05 + vec3(-uSmokeTime * 0.25, -uSmokeTime * .4, 0.0);
float n1 = smokeNoise(smokeP1);
float n2 = smokeNoise(smokeP2);
float pulse = 0.88 + 0.12 * sin(uSmokeTime * 3.2 + n2 * 6.2831853);
float smokeField = (n1 * 0.6 + n2 * 0.4) * pulse;
float smokeMask = smoothstep(0.18, 0.78, smokeField);
diffuseColor.a *= (0.35 + smokeMask * 0.65);`
      );
  };

  material.needsUpdate = true;
  smokeProceduralMaterials.push(material);
}

export function createVolcan3Effects(modelRoot) {
  const lavaFlowTextures = [];
  const chimneyTextures = [];
  const magmaFlowTextures = [];
  const emissivePulseTargets = [];
  const smokeColumns = [];
  const smokeProceduralMaterials = [];

  // 1) Recorrer materiales y preparar texturas animables + pulso
  const textureGroupMap = new Map();
  const seen = {
    lava: new Set(),
    chimney: new Set(),
    magma: new Set(),
    pulseMaterials: new Set(),
  };

  modelRoot.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const meshNameLower = (child.name || "").toLowerCase();

    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!material) return;
      const materialNameLower = (material.name || "").toLowerCase();

      ["map", "emissiveMap", "normalMap"].forEach((slot) => {
        let texture = material[slot];
        if (!texture) return;

        const textureNameLower = (getTextureName(texture) || "").toLowerCase();
        const group = getAnimationGroup(textureNameLower, materialNameLower, meshNameLower);
        if (!group) return;

        const existingGroup = textureGroupMap.get(texture);
        if (existingGroup && existingGroup !== group) {
          texture = texture.clone();
          texture.needsUpdate = true;
          material[slot] = texture;
        }
        if (!textureGroupMap.has(texture)) textureGroupMap.set(texture, group);

        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.needsUpdate = true;

        if (group === "lava" && !seen.lava.has(texture)) {
          lavaFlowTextures.push({ texture, slot });
          seen.lava.add(texture);
        }
        if (group === "chimney" && !seen.chimney.has(texture)) {
          chimneyTextures.push({ texture, slot });
          seen.chimney.add(texture);
        }
        if (group === "magma" && !seen.magma.has(texture)) {
          magmaFlowTextures.push({ texture, slot });
          seen.magma.add(texture);
        }

        if (group !== "chimney" && !seen.pulseMaterials.has(material)) {
          const baseEmissive = material.emissive
            ? material.emissive.clone()
            : new THREE.Color(0x000000);
          if (baseEmissive.r + baseEmissive.g + baseEmissive.b < 0.03) {
            baseEmissive.setRGB(1.0, 0.28, 0.04);
            if (material.emissive) material.emissive.copy(baseEmissive);
          }
          emissivePulseTargets.push({
            material,
            baseEmissive,
          });
          seen.pulseMaterials.add(material);
        }
      });
    });
  });

  // 2) Smoke_Column*
  const seenSmokeMeshes = new Set();
  modelRoot.traverse((child) => {
    if (!child.isMesh) return;
    const meshNameLower = (child.name || "").toLowerCase();
    if (!meshNameLower.includes("smoke_column")) return;
    if (seenSmokeMeshes.has(child)) return;
    seenSmokeMeshes.add(child);

    const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
    const materials = [];

    sourceMaterials.forEach((sourceMaterial) => {
      if (!sourceMaterial) return;
      const material = sourceMaterial.clone();
      material.transparent = true;
      material.depthWrite = false;
      material.opacity = 1.0;
      material.alphaTest = 0.0;
      material.map = null;
      material.alphaMap = null;
      material.normalMap = null;
      material.roughnessMap = null;
      material.metalnessMap = null;
      if (material.color) material.color.setRGB(0.92, 0.92, 0.92);
      attachProceduralSmoke(material, smokeProceduralMaterials);
      materials.push(material);
    });

    if (Array.isArray(child.material)) child.material = materials;
    else if (materials.length > 0) child.material = materials[0];

    smokeColumns.push({
      mesh: child,
      materials,
      baseScale: child.scale.clone(),
      baseRotY: child.rotation.y,
      basePosY: child.position.y,
      bounceAmp: Math.max(0.01, Math.abs(child.scale.y) * 0.06),
      bounceScaleAmp: 0.04,
      bounceFreq: 0.3375,
      phase: Math.random() * Math.PI * 2,
    });
  });

  const update = (delta, t) => {
    // lava (según main.js)
    for (const item of lavaFlowTextures) {
      const speedY = item.slot === "emissiveMap" ? 0 : 0;
      const speedX = item.slot === "normalMap" ? 0 : 1;
      item.texture.offset.y += delta * speedY;
      item.texture.offset.x -= delta * speedX;
    }

    // chimney
    for (const item of chimneyTextures) {
      const speedY = item.slot === "emissiveMap" ? 0 : 0.25;
      const speedX = item.slot === "normalMap" ? 0 : 0;
      item.texture.offset.y -= delta * speedY;
      item.texture.offset.x += delta * speedX;
    }

    // magma
    for (const item of magmaFlowTextures) {
      const magmaSpeedX = item.slot === "normalMap" ? 0 : 0;
      item.texture.offset.x += delta * magmaSpeedX;
    }

    // smoke
    for (const smoke of smokeColumns) {
      smoke.mesh.rotation.y = smoke.baseRotY;
      smoke.mesh.position.y =
        smoke.basePosY + Math.sin(t * smoke.bounceFreq + smoke.phase) * smoke.bounceAmp;
      const bounceScaleY =
        1.0 + Math.sin(t * smoke.bounceFreq + smoke.phase) * smoke.bounceScaleAmp;
      smoke.mesh.scale.set(smoke.baseScale.x, smoke.baseScale.y * bounceScaleY, smoke.baseScale.z);

      for (const material of smoke.materials) {
        if (!material) continue;
        material.opacity = 0.5;
      }
    }

    // uniforms procedural smoke
    for (const material of smokeProceduralMaterials) {
      const uniforms = material.userData?.smokeUniforms;
      if (uniforms?.uSmokeTime) uniforms.uSmokeTime.value = t;
    }

    // pulso emisivo (según main.js, muy marcado)
    for (const target of emissivePulseTargets) {
      const material = target.material;
      if (typeof material.emissiveIntensity !== "number") continue;
      const wave = (Math.sin(t * 2.0) + 1) * 0.5;
      material.emissiveIntensity = 0.1;
      if (target.baseEmissive && material.emissive) {
        material.emissive.copy(target.baseEmissive).multiplyScalar(0.1 + wave * 0.3);
      }
    }
  };

  return { update };
}

